import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Settings as SettingsIcon } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Card, CardContent } from '../ui/card';
import Actions from './Actions';

interface IConfigItem {
  key: string;
  value: string | boolean | number;
  type: string;
  isPublic: boolean;
  showInFeatureToggle: boolean;
  description: string;
}

const Toggle = ({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) => (
  <button
    onClick={onChange}
    disabled={disabled}
    className={`relative w-12 h-7 rounded-full transition-colors ${disabled ? 'bg-gray-200 opacity-50 cursor-not-allowed' : checked ? 'bg-[#030213]' : 'bg-gray-300'}`}
    role="switch"
    aria-checked={checked}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${checked && !disabled ? 'translate-x-5' : ''}`}
    />
  </button>
);

const FeatureTogglesSection = () => {
  const { t } = useTranslation();

  const [config, setConfig] = useState<IConfigItem[]>([]);
  const [dirty, setDirty] = useState<Record<string, string | boolean | number>>(
    {},
  );
  const [saving, setSaving] = useState(false);

  const configLabel = (key: string, dbDescription: string) => {
    const titleKey = `platform_config.${key}`;
    const descKey = `platform_config.${key}_desc`;
    const title = t(titleKey);
    const desc = t(descKey);

    return {
      title: title !== titleKey ? title : dbDescription || key,
      desc: desc !== descKey ? desc : dbDescription || key,
    };
  };

  useEffect(() => {
    const load = async () => {
      try {
        const items = await api.get<IConfigItem[]>(
          '/platform-config/backoffice',
        );
        setConfig(items);
      } catch (err) {
        console.error('Failed to load platform config:', err);
      }
    };
    load();
  }, []);

  const handleToggle = (key: string) => {
    const item = config.find((c) => c.key === key);
    const current = dirty[key] ?? item?.value ?? false;
    const newValue = !current;
    const updates: Record<string, string | boolean | number> = {
      [key]: newValue,
    };

    if (key === 'registration.requireMobileNumber' && !newValue) {
      const otpItem = config.find((c) => c.key === 'registration.otpMethod');
      const currentOtpMethod =
        (dirty['registration.otpMethod'] as string) ??
        (otpItem?.value as string) ??
        'off';
      if (currentOtpMethod === 'sms') {
        updates['registration.otpMethod'] = 'off';
      }
    }

    if (key === 'registration.requireEmail' && !newValue) {
      const otpItem = config.find((c) => c.key === 'registration.otpMethod');
      const currentOtpMethod =
        (dirty['registration.otpMethod'] as string) ??
        (otpItem?.value as string) ??
        'off';
      if (currentOtpMethod === 'email') {
        updates['registration.otpMethod'] = 'off';
      }
    }

    setDirty((prev) => ({ ...prev, ...updates }));
  };

  const handleValueChange = (key: string, value: string | number) => {
    const updates: Record<string, string | boolean | number> = { [key]: value };

    if (key === 'registration.otpMethod') {
      if (value === 'sms') {
        updates['registration.requireMobileNumber'] = true;
      } else if (value === 'email') {
        updates['registration.requireEmail'] = true;
      }
    }

    setDirty((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const entries = Object.entries(dirty).map(([key, value]) => ({
        key,
        value: String(value),
      }));

      if (entries.length > 0) {
        const updated = await api.patch<IConfigItem[]>(
          '/platform-config',
          entries,
        );
        setConfig(updated);

        if (entries.some((e) => e.key === 'x_crawl_frequency_minutes')) {
          await api.post('/admin/x-posts/refresh-schedule');
        }
      }

      setDirty({});
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const hasDirty = Object.keys(dirty).length > 0;
  const registrationFieldKeys = [
    'registration.requireEmail',
    'registration.requireMobileNumber',
  ];
  const registrationFieldItems = config.filter(
    (c) =>
      c.type === 'boolean' &&
      c.showInFeatureToggle &&
      registrationFieldKeys.includes(c.key),
  );
  const booleanItems = config.filter(
    (c) =>
      c.type === 'boolean' &&
      c.showInFeatureToggle &&
      !registrationFieldKeys.includes(c.key),
  );
  const inputItems = config.filter(
    (c) =>
      (c.type === 'number' || c.type === 'text') &&
      c.showInFeatureToggle &&
      c.key !== 'registration.otpMethod',
  );
  const otpMethodItem = config.find((c) => c.key === 'registration.otpMethod');

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-5">
          <SettingsIcon className="w-5 h-5 text-foreground" />
          <h3 className="text-base font-medium text-foreground">
            {t('config.feature_control')}
          </h3>
        </div>

        {/* Boolean toggles */}
        <div className="divide-y divide-gray-100">
          {booleanItems.map((item) => {
            const currentValue =
              (dirty[item.key] as boolean) ?? (item.value as boolean);
            const meta = configLabel(item.key, item.description);

            return (
              <div
                key={item.key}
                className="flex items-center justify-between py-4"
              >
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {meta.title}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    {meta.desc}
                  </div>
                </div>
                <Toggle
                  checked={currentValue}
                  onChange={() => handleToggle(item.key)}
                />
              </div>
            );
          })}
        </div>

        {/* Registration fields + OTP method grouped together */}
        {registrationFieldItems.length > 0 && (
          <div className="divide-y divide-gray-100 border-t border-border mt-4 pt-4">
            {registrationFieldItems.map((item) => {
              const currentValue =
                (dirty[item.key] as boolean) ?? (item.value as boolean);
              const meta = configLabel(item.key, item.description);

              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between py-4"
                >
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {meta.title}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5">
                      {meta.desc}
                    </div>
                  </div>
                  <Toggle
                    checked={currentValue}
                    onChange={() => handleToggle(item.key)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* OTP Method dropdown - right after registration fields */}
        {otpMethodItem &&
          (() => {
            const currentValue =
              dirty['registration.otpMethod'] ?? otpMethodItem.value;
            const meta = configLabel(
              otpMethodItem.key,
              otpMethodItem.description,
            );

            const mobileEnabled =
              (dirty['registration.requireMobileNumber'] as boolean) ??
              (config.find((c) => c.key === 'registration.requireMobileNumber')
                ?.value as boolean) ??
              false;
            const emailEnabled =
              (dirty['registration.requireEmail'] as boolean) ??
              (config.find((c) => c.key === 'registration.requireEmail')
                ?.value as boolean) ??
              false;

            const OTP_METHOD_OPTIONS = [
              ...(mobileEnabled
                ? [{ value: 'sms', label: t('config.otp_method.sms') }]
                : []),
              ...(emailEnabled
                ? [{ value: 'email', label: t('config.otp_method.email') }]
                : []),
              { value: 'off', label: t('config.otp_method.off') },
            ];

            return (
              <div className="py-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    {meta.title}
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {meta.desc}
                  </p>
                  <Select
                    value={String(currentValue)}
                    onValueChange={(val) =>
                      handleValueChange(otpMethodItem.key, val)
                    }
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OTP_METHOD_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()}

        {/* Number and text inputs */}
        {inputItems.length > 0 && (
          <div className="mt-4 space-y-5 border-t border-border pt-4">
            {inputItems.map((item) => {
              const currentValue = dirty[item.key] ?? item.value;
              const meta = configLabel(item.key, item.description);

              const FREQUENCY_OPTIONS = [
                { value: '5', label: t('config.frequency.5min') },
                { value: '10', label: t('config.frequency.10min') },
                { value: '15', label: t('config.frequency.15min') },
                { value: '20', label: t('config.frequency.20min') },
                { value: '30', label: t('config.frequency.30min') },
                { value: '60', label: t('config.frequency.1hour') },
                { value: '120', label: t('config.frequency.2hours') },
                { value: '180', label: t('config.frequency.3hours') },
                { value: '240', label: t('config.frequency.4hours') },
                { value: '360', label: t('config.frequency.6hours') },
                { value: '480', label: t('config.frequency.8hours') },
                { value: '720', label: t('config.frequency.12hours') },
                { value: '1440', label: t('config.frequency.24hours') },
              ];

              const isFrequencyField = item.key === 'x_crawl_frequency_minutes';

              return (
                <div key={item.key}>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    {meta.title}
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {meta.desc}
                  </p>
                  {isFrequencyField ? (
                    <Select
                      value={String(currentValue)}
                      onValueChange={(val) =>
                        handleValueChange(item.key, Number(val))
                      }
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={item.type === 'text' ? 'text' : 'number'}
                      value={currentValue as string | number}
                      onChange={(e) =>
                        handleValueChange(
                          item.key,
                          item.type === 'number'
                            ? Number(e.target.value)
                            : e.target.value,
                        )
                      }
                      placeholder={t('config.input_placeholder', {
                        field: meta.title,
                      })}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Actions
          onCancel={() => setDirty({})}
          onSave={handleSave}
          saving={saving}
          disabled={!hasDirty}
        />
      </CardContent>
    </Card>
  );
};

export default FeatureTogglesSection;
